import {
  Link,
  OverflowTooltip,
  Typography,
  boldBodySmallTypography,
  captionTypography,
  interaction60,
  logoIconColor,
  neutral60,
} from '@leaftrade/leaftrade-component-library';
import PropTypes from 'prop-types';
import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { currencyFormatter } from '../../../utils';
import {
  QuickOrderItemCellWrapper,
  QuickOrderItemDetails,
  QuickOrderItemImage,
} from './QuickOrderTable.styles';
import { getQuickOrderCurrentItem } from './quickOrderTableUtils';

const OVERFLOW_HEIGHT_OFFSET = 4;

const QuickOrderItemCell = ({
  batchQueuesEnabled,
  displayLiveInventoryCount,
  item,
  slug,
}) => {
  const currentItem = getQuickOrderCurrentItem({ batchQueuesEnabled, item });
  const productName = item.productName;
  const productId = item.productId;
  const imageUrl = item.imageUrl || currentItem.imageUrl || logoIconColor;
  const variantId = batchQueuesEnabled
    ? item.cartItemId
    : currentItem.variantId;
  const displayName = batchQueuesEnabled
    ? item.displayName
    : currentItem.displayName;
  const inventoryLiveCount = currentItem.inventoryLiveCount;
  const cannabinoids = batchQueuesEnabled ? [] : currentItem.cannabinoids || [];
  const cannabinoidUnit = currentItem.cannabinoidUnit || '';
  const batchRef = batchQueuesEnabled ? null : currentItem.batch_ref;
  const priceAndDisplayName = batchQueuesEnabled
    ? displayName
    : `${currencyFormatter.format(
        currentItem.storefrontDisplayPrice
      )} / ${displayName}`;

  return (
    <QuickOrderItemCellWrapper data-testid='quick-order-item-cell--component--div'>
      <QuickOrderItemImage src={imageUrl} alt='' />
      <QuickOrderItemDetails>
        <Link
          bodyStyle={boldBodySmallTypography}
          component={RouterLink}
          sx={{ color: interaction60.hex }}
          tabIndex={-1}
          title={productName}
          to={`/storefront/sellers/${slug}/${productId}?variant=${variantId}`}
          underline='hover'
        >
          <OverflowTooltip
            maxLineCount={2}
            tooltipId={`quick-order-item-name-${item.cartItemId}`}
            place='right'
          >
            {productName}
          </OverflowTooltip>
        </Link>
        <Typography bodyStyle={captionTypography} sx={{ color: neutral60.hex }}>
          <OverflowTooltip
            maxLineCount={1}
            overflowHeightOffset={OVERFLOW_HEIGHT_OFFSET}
            tooltipId={`quick-order-item-price-and-name-${item.cartItemId}`}
            place='right'
          >
            {priceAndDisplayName}
          </OverflowTooltip>
        </Typography>
        {!!cannabinoids.length && (
          <Typography
            bodyStyle={captionTypography}
            sx={{ color: neutral60.hex }}
          >
            <OverflowTooltip
              maxLineCount={1}
              overflowHeightOffset={OVERFLOW_HEIGHT_OFFSET}
              tooltipId={`quick-order-item-cannabinoids-${item.cartItemId}`}
              place='right'
            >
              {cannabinoids
                .map(
                  (cannabinoid) =>
                    `${cannabinoid.name} ${cannabinoid.value}${cannabinoidUnit}`
                )
                .join(' ')}
            </OverflowTooltip>
          </Typography>
        )}
        {batchRef && (
          <Typography
            bodyStyle={captionTypography}
            sx={{ color: neutral60.hex }}
          >
            <OverflowTooltip
              maxLineCount={1}
              overflowHeightOffset={OVERFLOW_HEIGHT_OFFSET}
              tooltipId={`quick-order-item-batch-${item.cartItemId}`}
              place='right'
            >
              Batch: {batchRef}
            </OverflowTooltip>
          </Typography>
        )}
        {displayLiveInventoryCount && (
          <Typography
            bodyStyle={captionTypography}
            sx={{ color: neutral60.hex }}
          >
            <OverflowTooltip
              maxLineCount={1}
              overflowHeightOffset={OVERFLOW_HEIGHT_OFFSET}
              tooltipId={`quick-order-item-inventory-${item.cartItemId}`}
              place='right'
            >
              Inv:{' '}
              {inventoryLiveCount >= 0
                ? inventoryLiveCount.toLocaleString()
                : 0}
            </OverflowTooltip>
          </Typography>
        )}
      </QuickOrderItemDetails>
    </QuickOrderItemCellWrapper>
  );
};

QuickOrderItemCell.propTypes = {
  batchQueuesEnabled: PropTypes.bool.isRequired,
  displayLiveInventoryCount: PropTypes.bool.isRequired,
  item: PropTypes.shape({
    cartItemId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    displayName: PropTypes.string,
    imageUrl: PropTypes.string,
    productId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    productName: PropTypes.string,
    variants: PropTypes.arrayOf(PropTypes.shape({})),
  }).isRequired,
  slug: PropTypes.string.isRequired,
};

export default QuickOrderItemCell;
